import React, { useEffect, useMemo, useState } from "react";
import InputField from "../../../Component/RetailerComponents/InputField";
import BlueButton from "../../../Component/Common/BlueButton";
import { BASE_URL } from "../../../config/api";
import toast from "react-hot-toast";




export default function PriceTemplateModal({
  isOpen,
  onClose,
  product,
  products,
  setProduct,
  onSave,
  errors,
  totalPayout,
}) {
  console.log("products: ", products)
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingTemplateId, setEditingTemplateId] = useState(null);

  const authUserString = localStorage.getItem("authUserData");
  const authUser = authUserString ? JSON.parse(authUserString) : null;

  const userRole = Number(authUser?.role); // 1 = admin, 2 = company
  const companyId = authUser?.id;  

  const isPercent = Number(product?.is_percent) === 1;

  const minvalue = Number(product?.min_price)

  const maxvalue = Number(product?.max_price)

  const companyBase = useMemo(() => {
  const mrp = Number(product?.mrp || 0);
  const admin = Number(product?.margin || 0);
  return mrp - admin;
}, [product?.mrp, product?.margin]);

const companyRemaining = useMemo(() => {
  const retailer = Number(product?.retailer_payout || 0);
  const agent = Number(product?.other_payout || 0);
  const emp = Number(product?.emp_payout || 0);

  return companyBase - retailer - agent - emp;
}, [
  companyBase,
  product?.retailer_payout,
  product?.other_payout,
  product?.emp_payout,
]);


  const remainingProfit = useMemo(() => {
  const mrp = Number(product?.mrp || 0);
  const admin = Number(product?.margin || 0);
  const emp = Number(product?.emp_payout || 0);
  const retailer = Number(product?.retailer_payout || 0);
  const agent = Number(product?.other_payout || 0);

  const remaining =
    mrp - admin - emp - retailer - agent;

  return remaining > 0 ? remaining : 0;
}, [
  product?.mrp,
  product?.margin,
  product?.emp_payout,
  product?.retailer_payout,
  product?.other_payout,
]);



  /* =========================
     FETCH ALL PRICE TEMPLATES
  ========================== */


    const fetchTemplates = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `${BASE_URL}warranty/price-templates?company_id=${companyId}`
        );
        const data = await res.json();
        if (!res.ok) throw new Error("Failed to load price templates");

        const matched = (data.price_templates || []).filter(
          (t) => Number(t.warranty_product_id) === Number(product.id)
        );

        setTemplates(matched);

        // ✅ If template exists → auto set next valid range
        // ✅ If template exists → auto set next valid range
        if (matched.length > 0) {
          const lastMax = Math.max(
            ...matched.map((t) => Number(t.max_price))
          );

          const productMax = Number(product.max_value);

          const isFullyCovered = lastMax >= productMax;

          setProduct((prev) => ({
            ...prev,
            minValue: isFullyCovered ? "" : lastMax + 1,
            maxValue: isFullyCovered ? "" : productMax,
          }));
        } else {
          setProduct((prev) => ({
            ...prev,
            minValue: Number(product.min_value),
            maxValue: Number(product.max_value),
          }));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
    if (!isOpen || !product?.id) return;

    fetchTemplates();
  }, [isOpen, product?.id]);

  /* =========================
     DERIVED RANGE LIMITS
  ========================== */
  const previousMax = useMemo(() => {
    if (!templates.length) return Number(product?.min_value || 0) - 1;
    return Math.max(...templates.map((t) => Number(t.max_price)));
  }, [templates, product?.min_value]);

  const minAllowed = previousMax + 1;
  const maxAllowed = Number(product?.max_value || 0);

  /* =========================
     RANGE OVERLAP CHECK
  ========================== */
 const hasOverlap = useMemo(() => {
  if (!product?.minValue || !product?.maxValue) return false;

  return templates.some(
    (t) =>
      t.id !== editingTemplateId && // 🔥 ignore template being edited
      Number(product.minValue) <= Number(t.max_price) &&
      Number(product.maxValue) >= Number(t.min_price)
  );
}, [product?.minValue, product?.maxValue, templates, editingTemplateId]);

  /* =========================
   PAYOUT CALCULATIONS
========================== */
// last (latest) template
const lastTemplate = useMemo(() => {
  if (!templates.length) return null;

  return templates.reduce((a, b) =>
    Number(b.max_price) > Number(a.max_price) ? b : a
  );
}, [templates]);

  const payoutMode = useMemo(() => {
    if (Number(product?.is_percent) === 1) return "percent";
    return "fixed"; // default
  }, [product?.is_percent]);

  const calculatedCompanyPayout = useMemo(() => {
  const mrp = Number(product?.mrp || 0);
  const admin = Number(product?.margin || 0);
  const emp = Number(product?.emp_payout || 0);
  const retailer = Number(product?.retailer_payout || 0);
  const other = Number(product?.other_payout || 0);

  // remaining after admin
  let remaining = mrp - admin;

  // subtract payouts
  let company = remaining - emp - retailer - other;

  return company >= 0 ? company : 0;
}, [
  product?.mrp,
  product?.margin,
  product?.emp_payout,
  product?.retailer_payout,
  product?.other_payout,
]);



  useEffect(() => {
  setProduct((p) => ({
    ...p,
    company_payout: companyRemaining > 0 ? companyRemaining : "",
  }));
}, [companyRemaining]);


 const isMaxLimitReached = useMemo(() => {
  if (!templates.length) return false;

  const otherTemplates = templates.filter(
    (t) => t.id !== editingTemplateId
  );

  if (!otherTemplates.length) return false;

  const highestTemplateMax = Math.max(
    ...otherTemplates.map((t) => Number(t.max_price))
  );

  return highestTemplateMax === Number(product?.max_value);
}, [templates, product?.max_value, editingTemplateId]);


  const hasMaxError = useMemo(() => {
  return companyRemaining <= 0;
}, [companyRemaining]);

  const maxPriceError = useMemo(() => {
  if (
    product?.minValue !== "" &&
    product?.maxValue !== "" &&
    Number(product?.maxValue) <= Number(product?.minValue)
  ) {
    return "Max price must be greater than Min price";
  }
  return "";
}, [product?.minValue, product?.maxValue]);



const updateTemplate = async () => {
  try {
    const payload = {
      warranty_product_id: product.id,
      min_price: product.minValue,
      max_price: product.maxValue,
      product_price: product.maxValue,
      emp_payout: product.emp_payout,
      retailer_payout: product.retailer_payout,
      other_payout: product.other_payout,
      company_payout: product.company_payout,
      company_id: companyId,
      is_fixed: product.is_fixed,
      is_percent: product.is_percent,
    };

    const res = await fetch(
      `${BASE_URL}warranty/update-price-template/${editingTemplateId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await res.json();

    if (!res.ok || !data?.status) {
      throw new Error(data?.message || "Update failed");
    }

    toast.success("Price template updated");

    // reset edit mode
    setEditingTemplateId(null);

    // reload templates
    await fetchTemplates();

  } catch (err) {
    toast.error(err.message);
  }
};

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[120vh] overflow-hidden relative">

        {/* CLOSE */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>

        {/* HEADER */}
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-bold">
            Price Templates — {product.name}
          </h2>
        </div>

        {/* BODY */}
        <div className="grid grid-cols-12 gap-4 p-6">

          {/* LEFT */}
          <div className="col-span-5 border-r pr-4">
            <h3 className="text-sm font-bold mb-3 text-slate-700">
              Existing Price Templates
            </h3>

            {loading && (
              <p className="text-sm text-slate-500">Loading templates...</p>
            )}

            {!loading && templates.length === 0 && (
              <p className="text-sm text-slate-500">
                No price templates created yet.
              </p>
            )}

            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
  {templates.map((t) => (
    <div
      key={t.id}
      className="rounded-xl border bg-slate-50 p-4 hover:shadow transition"
    >
      <div className="flex justify-between items-center mb-2">
        <span className="font-bold text-slate-900">
          ₹{t.min_price} – ₹{t.max_price}
          <span className="text-green-500 ml-1">Payout</span>
        </span>

        <span className="text-xs rounded-full bg-blue-100 text-blue-700 px-2 py-0.5">
          Active
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-3">
        <div>Admin: {isPercent ? `${product.margin}%` : `₹${product.margin}`}</div>
        <div>Promoter: {isPercent ? `${t.emp_payout}%` : `₹${t.emp_payout}`}</div>
        <div>Retailer: {isPercent ? `${t.retailer_payout}%` : `₹${t.retailer_payout}`}</div>
        <div>Agent: {isPercent ? `${t.other_payout}%` : `₹${t.other_payout}`}</div>
        <div>Company: {isPercent ? `${t.company_payout}%` : `₹${t.company_payout}`}</div>
      </div>

      {/* EDIT BUTTON */}
      <button
        onClick={() => {
          setEditingTemplateId(t.id);

          setProduct((prev) => ({
            ...prev,

            minValue: Number(t.min_price),
            maxValue: Number(t.max_price),

            emp_payout: Number(t.emp_payout),
            retailer_payout: Number(t.retailer_payout),
            other_payout: Number(t.other_payout),
            company_payout: Number(t.company_payout),

            is_percent: Number(t.is_percent),
            is_fixed: Number(t.is_fixed),
          }));
        }}
        className="text-xs px-3 py-1 rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
      >
        Edit
      </button>
    </div>
  ))}
</div>
          </div>

          {/* RIGHT */}
          <div className="col-span-7 pl-4">
            <h3 className="text-sm font-bold mb-3 text-slate-700">
              Create / Update Price Range
            </h3>

            {/* PRODUCT INFO */}
            <div className="rounded-xl bg-slate-100 p-4 mb-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Product</span>
                  <p className="font-bold">{product.name}</p>
                </div>
                <div>
                  <span className="text-slate-500">MRP</span>
                  <p className="font-bold">
                    {isPercent ? `${product.mrp}%` : `₹${product.mrp}`}
                  </p>

                </div>

                <div>
                  <span className="text-slate-500">Price Range Upto</span>
                  <p className="font-bold">
                    ₹{product.min_value} – ₹{product.max_value}
                  </p>

                </div>
              </div>
            </div>

            {/* RANGE INPUT */}
             <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold">Min Price</label>
                <InputField
                  type="number"
                  value={product.minValue ?? ""}
                  onChange={(v) => {
                    // allow typing freely
                    setProduct((p) => ({
                      ...p,
                      minValue: v === "" ? "" : Number(v),
                    }));
                  }}
                  onBlur={(e) => {
                    let value = Number(e.target.value);

                    // cannot be less than product.min_value
                    if (value < Number(product.min_value)) {
                      value = Number(product.min_value);
                    }

                    // cannot be greater than maxValue
                    if (product.maxValue && value > product.maxValue) {
                      value = product.maxValue;
                    }

                    setProduct((p) => ({
                      ...p,
                      minValue: value,
                    }));
                  }}
                />


                {errors?.min && (
                  <p className="text-xs text-red-600">{errors.min}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-semibold">Max Price</label>

                <InputField
                  type="number"
                  value={product.maxValue ?? ""}
                  onChange={(v) => {
                    // allow clear
                    if (v === "") {
                      setProduct((p) => ({ ...p, maxValue: "" }));
                      return;
                    }

                    // digits only
                    if (!/^\d+$/.test(String(v))) return;

                    const num = Number(v);

                    // 🚫 BLOCK if exceeds original product max
                    if (num > Number(product.max_value)) {
                      return;
                    }

                    setProduct((p) => ({
                      ...p,
                      maxValue: num,
                    }));
                  }}
                />

                {product.maxValue !== "" &&
                  Number(product.maxValue) > Number(product.max_value) && (
                    <p className="mt-1 text-xs text-red-600">
                      Max price cannot exceed ₹{product.max_value}
                    </p>
                )}
                
                {/* 🔴 INLINE ERROR */}
                {maxPriceError && (
                  <p className="mt-1 text-xs text-red-600">
                    {maxPriceError}
                  </p>
                )}
              </div>

            </div>

            {/* RANGE STATUS */}
            {product.minValue && product.maxValue && (
              <div
                className={`mt-3 rounded-lg p-2 text-xs font-semibold ${
                  hasOverlap || isMaxLimitReached
                    ? "bg-rose-50 text-rose-700"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {hasOverlap
                  ? "This range overlaps an existing template"
                  : isMaxLimitReached
                  ? "Maximum allowed price range already reached"
                  : "This range is available"}
              </div>
            )}

            {/* PAYOUTS */}
            <div className="grid grid-cols-2 gap-4 mt-4">

              <PayoutInput
                label="Admin Payout"
                value={product.margin}
                isPercent={isPercent}
                disabled
              />

              <PayoutInput
                label="Company Payout"
                value={product.company_payout}
                isPercent={isPercent}
                disabled   // 🔒 auto-calculated
              />

              <PayoutInput
                label="Promoter Payout (Coming Soon)"
                // value={product.emp_payout}
                disabled
                isPercent={isPercent}
                onChange={(v) =>
                  setProduct((p) => ({ ...p, emp_payout: v }))
                }
              />

              <PayoutInput
                label="Retailer Payout"
                value={product.retailer_payout}
                isPercent={isPercent}
                maxValue={companyBase - 1}
                onChange={(v) =>
                  setProduct((p) => ({ ...p, retailer_payout: v }))
                }
              />

              <PayoutInput
                label="Agent Payout"
                value={product.other_payout}
                isPercent={isPercent}
                maxValue={companyBase - 1}
                onChange={(v) =>
                  setProduct((p) => ({ ...p, other_payout: v }))
                }
              />


            </div>


            {/* PROFIT SUMMARY */}
            {/* <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-slate-100 p-2 text-xs">
                <p className="text-slate-500">Total Payout</p>
                <p className="font-bold">₹{calculatedTotalPayout}</p>
              </div>

              <div className="rounded-lg bg-emerald-50 p-2 text-xs">
                <p className="text-emerald-700">Remaining Profit</p>
                <p className="font-bold">₹{calculatedRemainingProfit}</p>
              </div>

              <div className="rounded-lg bg-blue-50 p-2 text-xs">
                <p className="text-blue-700">MRP</p>
                <p className="font-bold">₹{product.mrp}</p>
              </div>
            </div> */}


            {isMaxLimitReached && (
              <p className="text-xs text-amber-600 mt-2 text-right">
                You have already reached the maximum allowed price range for this product.
              </p>
            )}

            {/* ACTION */}
            <div className="mt-6 flex justify-end">
              <BlueButton
                disabled={hasOverlap || isMaxLimitReached || hasMaxError || !!maxPriceError}
                onClick={editingTemplateId ? updateTemplate : onSave}
                className="px-6"
              >
                {editingTemplateId
                ? "Update Price Template"
                : templates.length
                ? "Add New Price Range"
                : "Create Price Template"}
              </BlueButton>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PayoutInput = ({
  label,
  value,
  onChange,
  isPercent,
  disabled,
  maxValue,
}) => {
  const maxAllowed = isPercent ? 100 : maxValue;

  const numericValue =
    value === "" || value === undefined ? null : Number(value);

  const showMaxError =
    numericValue !== null &&
    maxAllowed !== undefined &&
    numericValue > maxAllowed;

  return (
    <div>
      <label className="text-sm font-semibold">
        {label} {isPercent ? "(%)" : "(₹)"}
      </label>

      <div className="relative">
        <InputField
          type="number"
          value={value ?? ""}
          disabled={disabled}
          onChange={(raw) => {
            // allow clear
            if (raw === "") {
              onChange("");
              return;
            }

            // digits only
            if (!/^\d+$/.test(raw)) return;

            const num = Number(raw);

            // 🚫 HARD STOP — DO NOT UPDATE STATE
            if (
              maxAllowed !== undefined &&
              maxAllowed !== null &&
              num > maxAllowed
            ) {
              return;
            }

            onChange(raw);
          }}

        />

        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
          {isPercent ? "%" : "₹"}
        </span>
      </div>

      {/* ERROR MESSAGE */}
      {showMaxError && (
        <p className="mt-1 text-xs text-red-600">
          Company payout must be greater than ₹0
        </p>
      )}
    </div>
  );
};



// import React, { useEffect, useMemo, useState } from "react";
// import InputField from "../../../Component/RetailerComponents/InputField";
// import BlueButton from "../../../Component/Common/BlueButton";
// import { BASE_URL } from "../../../config/api";




// export default function PriceTemplateModal({
//   isOpen,
//   onClose,
//   product,
//   products,
//   setProduct,
//   onSave,
//   errors,
//   totalPayout,
// }) {
//   console.log("products: ", products)
//   const [templates, setTemplates] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   const userRole = Number(authUser?.role); // 1 = admin, 2 = company
//   const companyId = authUser?.id;  

//   const isPercent = Number(product?.is_percent) === 1;

//   const minvalue = Number(product?.min_price)

//   const maxvalue = Number(product?.max_price)

//   const companyBase = useMemo(() => {
//   const mrp = Number(product?.mrp || 0);
//   const admin = Number(product?.margin || 0);
//   return mrp - admin;
// }, [product?.mrp, product?.margin]);

// const companyRemaining = useMemo(() => {
//   const retailer = Number(product?.retailer_payout || 0);
//   const agent = Number(product?.other_payout || 0);
//   const emp = Number(product?.emp_payout || 0);

//   return companyBase - retailer - agent - emp;
// }, [
//   companyBase,
//   product?.retailer_payout,
//   product?.other_payout,
//   product?.emp_payout,
// ]);


//   const remainingProfit = useMemo(() => {
//   const mrp = Number(product?.mrp || 0);
//   const admin = Number(product?.margin || 0);
//   const emp = Number(product?.emp_payout || 0);
//   const retailer = Number(product?.retailer_payout || 0);
//   const agent = Number(product?.other_payout || 0);

//   const remaining =
//     mrp - admin - emp - retailer - agent;

//   return remaining > 0 ? remaining : 0;
// }, [
//   product?.mrp,
//   product?.margin,
//   product?.emp_payout,
//   product?.retailer_payout,
//   product?.other_payout,
// ]);



//   /* =========================
//      FETCH ALL PRICE TEMPLATES
//   ========================== */
//   useEffect(() => {
//     if (!isOpen || !product?.id) return;

//     const fetchTemplates = async () => {
//       try {
//         setLoading(true);

//         const res = await fetch(
//           `${BASE_URL}warranty/price-templates?company_id=${companyId}`
//         );
//         const data = await res.json();
//         if (!res.ok) throw new Error("Failed to load price templates");

//         const matched = (data.price_templates || []).filter(
//           (t) => Number(t.warranty_product_id) === Number(product.id)
//         );

//         setTemplates(matched);

//         // ✅ If template exists → auto set next valid range
//         // ✅ If template exists → auto set next valid range
//         if (matched.length > 0) {
//           const lastMax = Math.max(
//             ...matched.map((t) => Number(t.max_price))
//           );

//           const productMax = Number(product.max_value);

//           const isFullyCovered = lastMax >= productMax;

//           setProduct((prev) => ({
//             ...prev,
//             minValue: isFullyCovered ? "" : lastMax + 1,
//             maxValue: isFullyCovered ? "" : productMax,
//             is_percent: Number(matched[0].is_percent),
//             is_fixed: Number(matched[0].is_fixed),
//           }));
//         } else {
//           // ✅ NO existing template → show product min & max
//           setProduct((prev) => ({
//             ...prev,
//             minValue: Number(product.min_value),
//             maxValue: Number(product.max_value),
//           }));
//         }
//       } catch (err) {
//         console.error(err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchTemplates();
//   }, [isOpen, product?.id]);

//   /* =========================
//      DERIVED RANGE LIMITS
//   ========================== */
//   const previousMax = useMemo(() => {
//     if (!templates.length) return Number(product?.min_value || 0) - 1;
//     return Math.max(...templates.map((t) => Number(t.max_price)));
//   }, [templates, product?.min_value]);

//   const minAllowed = previousMax + 1;
//   const maxAllowed = Number(product?.max_value || 0);

//   /* =========================
//      RANGE OVERLAP CHECK
//   ========================== */
//   const hasOverlap = useMemo(() => {
//     if (!product?.minValue || !product?.maxValue) return false;

//     return templates.some(
//       (t) =>
//         Number(product.minValue) <= Number(t.max_price) &&
//         Number(product.maxValue) >= Number(t.min_price)
//     );
//   }, [product?.minValue, product?.maxValue, templates]);

//   /* =========================
//    PAYOUT CALCULATIONS
// ========================== */
// // last (latest) template
// const lastTemplate = useMemo(() => {
//   if (!templates.length) return null;

//   return templates.reduce((a, b) =>
//     Number(b.max_price) > Number(a.max_price) ? b : a
//   );
// }, [templates]);

//   const payoutMode = useMemo(() => {
//     if (Number(product?.is_percent) === 1) return "percent";
//     return "fixed"; // default
//   }, [product?.is_percent]);

//   const calculatedCompanyPayout = useMemo(() => {
//   const mrp = Number(product?.mrp || 0);
//   const admin = Number(product?.margin || 0);
//   const emp = Number(product?.emp_payout || 0);
//   const retailer = Number(product?.retailer_payout || 0);
//   const other = Number(product?.other_payout || 0);

//   // remaining after admin
//   let remaining = mrp - admin;

//   // subtract payouts
//   let company = remaining - emp - retailer - other;

//   return company >= 0 ? company : 0;
// }, [
//   product?.mrp,
//   product?.margin,
//   product?.emp_payout,
//   product?.retailer_payout,
//   product?.other_payout,
// ]);



//   useEffect(() => {
//   setProduct((p) => ({
//     ...p,
//     company_payout: companyRemaining > 0 ? companyRemaining : "",
//   }));
// }, [companyRemaining]);


//  const isMaxLimitReached = useMemo(() => {
//   if (!templates.length) return false;

//   const highestTemplateMax = Math.max(
//     ...templates.map((t) => Number(t.max_price))
//   );

//   return highestTemplateMax === Number(product?.max_value);
// }, [templates, product?.max_value]);


//   const hasMaxError = useMemo(() => {
//   return companyRemaining <= 0;
// }, [companyRemaining]);

//   const maxPriceError = useMemo(() => {
//   if (
//     product?.minValue !== "" &&
//     product?.maxValue !== "" &&
//     Number(product?.maxValue) <= Number(product?.minValue)
//   ) {
//     return "Max price must be greater than Min price";
//   }
//   return "";
// }, [product?.minValue, product?.maxValue]);



//   if (!isOpen || !product) return null;

//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//       <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[120vh] overflow-hidden relative">

//         {/* CLOSE */}
//         <button
//           onClick={onClose}
//           className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
//         >
//           ✕
//         </button>

//         {/* HEADER */}
//         <div className="border-b px-6 py-4">
//           <h2 className="text-lg font-bold">
//             Price Templates — {product.name}
//           </h2>
//         </div>

//         {/* BODY */}
//         <div className="grid grid-cols-12 gap-4 p-6">

//           {/* LEFT */}
//           <div className="col-span-5 border-r pr-4">
//             <h3 className="text-sm font-bold mb-3 text-slate-700">
//               Existing Price Templates
//             </h3>

//             {loading && (
//               <p className="text-sm text-slate-500">Loading templates...</p>
//             )}

//             {!loading && templates.length === 0 && (
//               <p className="text-sm text-slate-500">
//                 No price templates created yet.
//               </p>
//             )}

//             <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
//               {templates.map((t) => (
//                 <div
//                   key={t.id}
//                   className="rounded-xl border bg-slate-50 p-4 hover:shadow transition"
//                 >
//                   <div className="flex justify-between items-center mb-2">
//                     <span className="font-bold text-slate-900">
//                       ₹{t.min_price} – ₹{t.max_price} <span className="text-green-500">Payout</span>
//                       </span>
//                     <span className="text-xs rounded-full bg-blue-100 text-blue-700 px-2 py-0.5">
//                       Active
//                     </span>
//                   </div>

//                   <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
//                     <div>Admin: {isPercent ? `${product.margin}%` : `₹${product.margin}`}</div>
//                     <div>Promoter: {isPercent ? `${t.emp_payout}%` : `₹${t.emp_payout}`}</div>
//                     <div>Retailer: {isPercent ? `${t.retailer_payout}%` : `₹${t.retailer_payout}`}</div>
//                     <div>Agent: {isPercent ? `${t.other_payout}%` : `₹${t.other_payout}`}</div>
//                     <div>Company: {isPercent ? `${t.company_payout}%` : `₹${t.company_payout}`}</div>

//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* RIGHT */}
//           <div className="col-span-7 pl-4">
//             <h3 className="text-sm font-bold mb-3 text-slate-700">
//               Create / Update Price Range
//             </h3>

//             {/* PRODUCT INFO */}
//             <div className="rounded-xl bg-slate-100 p-4 mb-4">
//               <div className="grid grid-cols-3 gap-3 text-sm">
//                 <div>
//                   <span className="text-slate-500">Product</span>
//                   <p className="font-bold">{product.name}</p>
//                 </div>
//                 <div>
//                   <span className="text-slate-500">MRP</span>
//                   <p className="font-bold">
//                     {isPercent ? `${product.mrp}%` : `₹${product.mrp}`}
//                   </p>

//                 </div>

//                 <div>
//                   <span className="text-slate-500">Price Range Upto</span>
//                   <p className="font-bold">
//                     ₹{product.min_value} – ₹{product.max_value}
//                   </p>

//                 </div>
//               </div>
//             </div>

//             {/* RANGE INPUT */}
//              <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <label className="text-sm font-semibold">Min Price</label>
//                 <InputField
//                   type="number"
//                   value={product.minValue ?? ""}
//                   onChange={(v) => {
//                     // allow typing freely
//                     setProduct((p) => ({
//                       ...p,
//                       minValue: v === "" ? "" : Number(v),
//                     }));
//                   }}
//                   onBlur={(e) => {
//                     let value = Number(e.target.value);

//                     // cannot be less than product.min_value
//                     if (value < Number(product.min_value)) {
//                       value = Number(product.min_value);
//                     }

//                     // cannot be greater than maxValue
//                     if (product.maxValue && value > product.maxValue) {
//                       value = product.maxValue;
//                     }

//                     setProduct((p) => ({
//                       ...p,
//                       minValue: value,
//                     }));
//                   }}
//                 />


//                 {errors?.min && (
//                   <p className="text-xs text-red-600">{errors.min}</p>
//                 )}
//               </div>

//               <div>
//                 <label className="text-sm font-semibold">Max Price</label>

//                 <InputField
//                   type="number"
//                   value={product.maxValue ?? ""}
//                   onChange={(v) => {
//                     // allow clear
//                     if (v === "") {
//                       setProduct((p) => ({ ...p, maxValue: "" }));
//                       return;
//                     }

//                     // digits only
//                     if (!/^\d+$/.test(String(v))) return;

//                     const num = Number(v);

//                     // 🚫 BLOCK if exceeds original product max
//                     if (num > Number(product.max_value)) {
//                       return;
//                     }

//                     setProduct((p) => ({
//                       ...p,
//                       maxValue: num,
//                     }));
//                   }}
//                 />

//                 {product.maxValue !== "" &&
//                   Number(product.maxValue) > Number(product.max_value) && (
//                     <p className="mt-1 text-xs text-red-600">
//                       Max price cannot exceed ₹{product.max_value}
//                     </p>
//                 )}
                
//                 {/* 🔴 INLINE ERROR */}
//                 {maxPriceError && (
//                   <p className="mt-1 text-xs text-red-600">
//                     {maxPriceError}
//                   </p>
//                 )}
//               </div>

//             </div>

//             {/* RANGE STATUS */}
//             {product.minValue && product.maxValue && (
//               <div
//                 className={`mt-3 rounded-lg p-2 text-xs font-semibold ${
//                   hasOverlap || isMaxLimitReached
//                     ? "bg-rose-50 text-rose-700"
//                     : "bg-emerald-50 text-emerald-700"
//                 }`}
//               >
//                 {hasOverlap
//                   ? "This range overlaps an existing template"
//                   : isMaxLimitReached
//                   ? "Maximum allowed price range already reached"
//                   : "This range is available"}
//               </div>
//             )}

//             {/* PAYOUTS */}
//             <div className="grid grid-cols-2 gap-4 mt-4">

//               <PayoutInput
//                 label="Admin Payout"
//                 value={product.margin}
//                 isPercent={isPercent}
//                 disabled
//               />

//               <PayoutInput
//                 label="Company Payout"
//                 value={product.company_payout}
//                 isPercent={isPercent}
//                 disabled   // 🔒 auto-calculated
//               />

//               <PayoutInput
//                 label="Promoter Payout (Coming Soon)"
//                 // value={product.emp_payout}
//                 disabled
//                 isPercent={isPercent}
//                 onChange={(v) =>
//                   setProduct((p) => ({ ...p, emp_payout: v }))
//                 }
//               />

//               <PayoutInput
//                 label="Retailer Payout"
//                 value={product.retailer_payout}
//                 isPercent={isPercent}
//                 maxValue={companyBase - 1}
//                 onChange={(v) =>
//                   setProduct((p) => ({ ...p, retailer_payout: v }))
//                 }
//               />

//               <PayoutInput
//                 label="Agent Payout"
//                 value={product.other_payout}
//                 isPercent={isPercent}
//                 maxValue={companyBase - 1}
//                 onChange={(v) =>
//                   setProduct((p) => ({ ...p, other_payout: v }))
//                 }
//               />


//             </div>


//             {/* PROFIT SUMMARY */}
//             {/* <div className="mt-4 grid grid-cols-3 gap-3">
//               <div className="rounded-lg bg-slate-100 p-2 text-xs">
//                 <p className="text-slate-500">Total Payout</p>
//                 <p className="font-bold">₹{calculatedTotalPayout}</p>
//               </div>

//               <div className="rounded-lg bg-emerald-50 p-2 text-xs">
//                 <p className="text-emerald-700">Remaining Profit</p>
//                 <p className="font-bold">₹{calculatedRemainingProfit}</p>
//               </div>

//               <div className="rounded-lg bg-blue-50 p-2 text-xs">
//                 <p className="text-blue-700">MRP</p>
//                 <p className="font-bold">₹{product.mrp}</p>
//               </div>
//             </div> */}


//             {isMaxLimitReached && (
//               <p className="text-xs text-amber-600 mt-2 text-right">
//                 You have already reached the maximum allowed price range for this product.
//               </p>
//             )}

//             {/* ACTION */}
//             <div className="mt-6 flex justify-end">
//               <BlueButton
//                 disabled={hasOverlap || isMaxLimitReached || hasMaxError || !!maxPriceError}
//                 onClick={onSave}
//                 className="px-6"
//               >
//                 {isMaxLimitReached
//                   ? "Max Price Range Reached"
//                   : templates.length
//                   ? "Add New Price Range"
//                   : "Create Price Template"}
//               </BlueButton>

//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// const PayoutInput = ({
//   label,
//   value,
//   onChange,
//   isPercent,
//   disabled,
//   maxValue,
// }) => {
//   const maxAllowed = isPercent ? 100 : maxValue;

//   const numericValue =
//     value === "" || value === undefined ? null : Number(value);

//   const showMaxError =
//     numericValue !== null &&
//     maxAllowed !== undefined &&
//     numericValue > maxAllowed;

//   return (
//     <div>
//       <label className="text-sm font-semibold">
//         {label} {isPercent ? "(%)" : "(₹)"}
//       </label>

//       <div className="relative">
//         <InputField
//           type="number"
//           value={value ?? ""}
//           disabled={disabled}
//           onChange={(raw) => {
//             // allow clear
//             if (raw === "") {
//               onChange("");
//               return;
//             }

//             // digits only
//             if (!/^\d+$/.test(raw)) return;

//             const num = Number(raw);

//             // 🚫 HARD STOP — DO NOT UPDATE STATE
//             if (
//               maxAllowed !== undefined &&
//               maxAllowed !== null &&
//               num > maxAllowed
//             ) {
//               return;
//             }

//             onChange(raw);
//           }}

//         />

//         <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
//           {isPercent ? "%" : "₹"}
//         </span>
//       </div>

//       {/* ERROR MESSAGE */}
//       {showMaxError && (
//         <p className="mt-1 text-xs text-red-600">
//           Company payout must be greater than ₹0
//         </p>
//       )}
//     </div>
//   );
// };

